import { shape, string, arrayOf } from 'prop-types';
import { SaplingUtil } from '@axiom/ui';

import { CandidateApi, getCandidate } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';
import { CertificationShape } from '../../models/certification-model';
import RecordStateHelper from '../../lib/record-state-helper';
import SubmitStateHelper from '../../lib/submit-state-helper';
import { PreloadedFeatureFlagsStore } from '../preloaded-feature-flags-store';
import { SaplingApi } from '../../api/sapling-api';

import { CandidateStore } from './candidate-store';

export const CURRENT_CANDIDATE_NAMESPACE = 'CURRENT_CANDIDATE';

const stateGetCandidate = new RecordStateHelper(
  CURRENT_CANDIDATE_NAMESPACE,
  'FETCH'
);

const stateDeleteAccomplishment = new SubmitStateHelper(
  CURRENT_CANDIDATE_NAMESPACE,
  'DELETE_CERTIFICATE'
);

const stateDeleteEducationItem = new SubmitStateHelper(
  CURRENT_CANDIDATE_NAMESPACE,
  'DELETE_EDUCATION_ITEM'
);

const mutateCandidate = candidate => {
  /**
   * Construct full name prop
   */
  const name = [
    candidate.nickName && candidate.nickName.length > 0
      ? candidate.nickName
      : candidate.firstName,
    candidate.lastName,
  ];

  if (candidate.middleName && candidate.middleName.length > 0) {
    name.splice(1, 0, candidate.middleName);
  }

  candidate.fullName = name.join(' ');

  candidate.practicingYears = candidate?.practiceStartYear
    ? new Date().getFullYear() - candidate.practiceStartYear
    : null;

  /**
   * Add profileImageUrl to candidate if they have one
   */
  candidate.profileImageUrl =
    candidate.profileImageKey && candidate.profileImageName
      ? CandidateApi.readProfileImageUrl(candidate.id)
      : null;

  return candidate;
};

export const CurrentCandidateStore = {
  getInitialState() {
    return {
      data: {
        record: {},
      },
    };
  },
  getDataShape() {
    return shape({
      data: shape({
        record: shape({
          id: string,
        }),
        certifications: arrayOf(CertificationShape),
      }),
    });
  },
  getReducers() {
    return {
      ...stateGetCandidate.generateReducers(),
      ...stateDeleteAccomplishment.generateReducers(),
      ...stateDeleteEducationItem.generateReducers(),
    };
  },
  actionFetchCandidate(candidateId, user, loadFeatureFlags = false) {
    return dispatch =>
      dispatch(
        stateGetCandidate.fetchRecord(
          Promise.all([
            getCandidate(candidateId),
            SaplingApi.createToken(),
          ]).then(([candidate, tokenWrapper]) => {
            CandidateStore.load(candidate);
            if (loadFeatureFlags) {
              // user is real (not undefined) here, see AppStore.initTalentAppState
              PreloadedFeatureFlagsStore.bootstrap({ user, candidate });
            }
            SaplingUtil.initialize(tokenWrapper.token, user?.id);
            CandidateApi.refreshCandidate(candidateId);
            CandidateProfileIssuesApi.refreshCandidateProfileIssues(
              candidateId
            );

            return {
              candidateId: candidate.id,
              record: mutateCandidate(candidate),
              certifications: candidate.certifications || [],
              barredLocations: candidate.barredLocations,
            };
          })
        )
      );
  },
  actionUpdateCandidate(candidateId, props) {
    return dispatch =>
      CandidateApi.updateCandidate(candidateId, props).then(({ data }) => {
        CandidateStore.load(data);
        CandidateApi.refreshCandidate(candidateId);
        CandidateProfileIssuesApi.refreshCandidateProfileIssues(candidateId);

        dispatch(
          stateGetCandidate.fetchRecord(
            Promise.resolve({
              candidateId: data.id,
              record: mutateCandidate(data),
              certifications: data.certifications || [],
              barredLocations: data.barredLocations,
            })
          )
        );
      });
  },
  selectCandidateState(state) {
    return stateGetCandidate.select(state);
  },
  selectCandidate(state) {
    return stateGetCandidate.selectData(state);
  },
};
